import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Text } from "@zorni/components-lib";
import isEqual from "lodash/isEqual";

import globalStyles from "../../../../assets/css/zorni-administration.module.css";
import styles from "./AddTrip.module.css";
import { useAdmin } from "../../../hooks/useAdmin";
import { useOperation } from "../../../hooks/useOperation";
import { useUpload } from "../../../../common/hooks/useUpload";
import Preloader from "../../../../common/preLoader/PreLoader";
import { useAddTrip } from "../hooks/useAddTrip";
import { useEditTrip } from "../hooks/useEditTrip";
import { useTrip } from "../hooks/useTrip";
import GeneralInfoCard from "./GeneralInfoCard/GeneralInfoCard";
import ProgramStepFormCard from "./ProgramStepFormCard/ProgramStepFormCard";
import ItineraryStepsCard from "./ItineraryStepsCard/ItineraryStepsCard";

const TABS = { GENERAL: "general", PROGRAM: "program" };

const emptyItem = (isActivity) => ({
  title: "",
  description: "",
  picture: "",
  isIncluded: isActivity ? false : undefined,
  adultPricePerPerson: { value: "", currency: "MAD" },
  childPricePerPerson: { value: "", currency: "MAD" },
  babiesPricePerPerson: { value: "", currency: "MAD" },
});

const emptyDraft = () => ({
  rankingTitle: "",
  rankingRank: 1,
  city: "",
  country: "",
  latitude: null,
  longitude: null,
  stay: false,
  hostingName: "",
  hostingStars: 0,
  description: "",
  pictureLink: "",
  isMain: false,
  activities: [],
  options: [],
});

const num = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};
const nullableNum = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const buildReference = (title) => {
  const clean = (title || "trip")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${clean || "trip"}-${Date.now()}`;
};

const AddTrip = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tripId, publicationId } = useParams();
  const { accessToken, validEstablishments } = useAdmin();
  const { showToast, triggerRefresh } = useOperation();
  const { deleteFile, getUploadIdFromUrl } = useUpload();

  const [trip] = useState(tripId || null);
  const [activeTab, setActiveTab] = useState(TABS.GENERAL);

  // Central state for all inputs in this page
  const [establishments, setEstablishments] = useState([]);
  const [establishment, setEstablishment] = useState({});
  const [generalInfo, setGeneralInfo] = useState({ title: "", smallDescription: "", description: "" });
  const [tripSteps, setTripSteps] = useState([]);
  const [editingStepIndex, setEditingStepIndex] = useState(null);
  const [stepDraft, setStepDraft] = useState(emptyDraft());
  const [stepErrors, setStepErrors] = useState({});
  const [validationError, setValidationError] = useState("");
  const [loadedTrip, setLoadedTrip] = useState(null);
  const [expandedTripSteps, setExpandedTripSteps] = useState({});
  const [expandedDraftActivities, setExpandedDraftActivities] = useState({});
  const [expandedDraftOptions, setExpandedDraftOptions] = useState({});
  const [draggedStepIndex, setDraggedStepIndex] = useState(null);
  const hydratedTripIdRef = useRef(null);
  const hasEstablishment = Boolean(establishment?.id);
  const { addTrip, loading: addTripLoading } = useAddTrip(accessToken);
  const { updateTrip, loading: updateTripLoading } = useEditTrip(accessToken);
  const {
    trip: currentTrip,
    loading: tripDataLoading,
    error: tripDataError,
  } = useTrip({
    tripId: trip,
    accessToken,
  });
  const loading = tripDataLoading || addTripLoading || updateTripLoading;

  // Établissements ACTIVATED de la liste centrale (useAdmin).
  useEffect(() => {
    setEstablishments(validEstablishments);
  }, [validEstablishments]);

  useEffect(() => {
    if (!currentTrip?.id) return;
    if (hydratedTripIdRef.current === currentTrip.id) return;

    hydratedTripIdRef.current = currentTrip.id;
    setLoadedTrip(currentTrip);
    setGeneralInfo({
      title: currentTrip.title || "",
      smallDescription: currentTrip.smallDescription || "",
      description: currentTrip.detailedDescription || "",
    });
    setTripSteps(currentTrip.tripProgramDetails || []);
    setEstablishment(
      currentTrip.establishmentId
        ? {
            id: currentTrip.establishmentId,
            name: "",
          }
        : {},
    );
    setActiveTab(TABS.PROGRAM);
  }, [currentTrip]);

  useEffect(() => {
    if (!establishment?.id || establishment?.name || !establishments.length) return;
    const selectedEstablishment = establishments.find((item) => item.id === establishment.id);
    if (!selectedEstablishment) return;

    setEstablishment((prev) => ({
      ...prev,
      name: selectedEstablishment.company?.legalName,
    }));
  }, [establishment, establishments]);

  useEffect(() => {
    if (tripDataError) {
      showToast(t("tripPublication.addTrip.toasts.loadError"), "error");
    }
  }, [tripDataError, showToast, t]);

  useEffect(() => {
    if (editingStepIndex !== null) return;
    const nextRank = tripSteps.length + 1;
    setStepDraft((prev) =>
      prev.rankingRank === nextRank ? prev : { ...prev, rankingRank: nextRank }
    );
  }, [tripSteps.length, editingStepIndex]);

  useEffect(() => {
    if (!hasEstablishment && activeTab === TABS.PROGRAM) {
      setActiveTab(TABS.GENERAL);
    }
  }, [hasEstablishment, activeTab]);

  const updateDraft = (field, value) => setStepDraft((p) => ({ ...p, [field]: value }));

  const updateSubField = (group, idx, field, value) => {
    setStepDraft((p) => {
      const next = [...p[group]];
      next[idx] = { ...next[idx], [field]: value };
      return { ...p, [group]: next };
    });
  };

  const updateSubPrice = (group, idx, priceKey, key, value) => {
    setStepDraft((p) => {
      const next = [...p[group]];
      next[idx] = {
        ...next[idx],
        [priceKey]: { ...next[idx][priceKey], [key]: value },
      };
      return { ...p, [group]: next };
    });
  };
  const addSubItem = (group) => {
    setStepDraft((p) => ({ ...p, [group]: [...p[group], emptyItem(group === "activities")] }));
    if (group === "activities") {
      const nextIdx = stepDraft.activities.length;
      setExpandedDraftActivities((prev) => ({ ...prev, [nextIdx]: true }));
    } else {
      const nextIdx = stepDraft.options.length;
      setExpandedDraftOptions((prev) => ({ ...prev, [nextIdx]: true }));
    }
  };

  const removeUploadedByUrl = async (url) => {
    const uploadId = getUploadIdFromUrl(url);
    if (!uploadId) return;
    try {
      await deleteFile(uploadId);
    } catch (err) {
      console.error("Error deleting file:", err);
      showToast(
        t("tripPublication.addTrip.upload.deleteError", "Le fichier n'a pas pu être supprimé."),
        "error"
      );
    }
  };

  const removeSubItem = async (group, idx) => {
    const target = stepDraft[group]?.[idx];
    if (target?.picture) {
      await removeUploadedByUrl(target.picture);
    }

    setStepDraft((p) => ({ ...p, [group]: p[group].filter((_, i) => i !== idx) }));
    if (group === "activities") {
      setExpandedDraftActivities((prev) => {
        const { [idx]: _, ...rest } = prev;
        return rest;
      });
    } else {
      setExpandedDraftOptions((prev) => {
        const { [idx]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const toggleActivityIncluded = (idx, checked) => {
    setStepDraft((prev) => {
      const activities = [...prev.activities];
      const current = activities[idx];
      if (!current) return prev;

      activities[idx] = {
        ...current,
        isIncluded: checked,
        adultPricePerPerson: {
          ...current.adultPricePerPerson,
          value: checked ? 0 : current.adultPricePerPerson?.value ?? "",
          currency: current.adultPricePerPerson?.currency || "MAD",
        },
        childPricePerPerson: {
          ...current.childPricePerPerson,
          value: checked ? 0 : current.childPricePerPerson?.value ?? "",
          currency: current.childPricePerPerson?.currency || "MAD",
        },
        babiesPricePerPerson: {
          ...current.babiesPricePerPerson,
          value: checked ? 0 : current.babiesPricePerPerson?.value ?? "",
          currency: current.babiesPricePerPerson?.currency || "MAD",
        },
      };

      return { ...prev, activities };
    });
  };

  const mapItem = (item, isActivity) => ({
    ...(item.id ? { id: item.id } : {}),
    title: item.title || "",
    description: item.description || "",
    picture: item.picture || "",
    ...(isActivity ? { isIncluded: Boolean(item.isIncluded) } : {}),
    adultPricePerPerson: {
      value: num(item.adultPricePerPerson?.value),
      currency: item.adultPricePerPerson?.currency || "MAD",
    },
    childPricePerPerson: {
      value: num(item.childPricePerPerson?.value),
      currency: item.childPricePerPerson?.currency || "MAD",
    },
    babiesPricePerPerson: {
      value: num(item.babiesPricePerPerson?.value),
      currency: item.babiesPricePerPerson?.currency || "MAD",
    },
  });

  const addFieldError = (target, key, message) => {
    if (!target[key]) target[key] = [];
    target[key].push(message);
  };

  const validatePrice = (value, priceLabel, target, key, allowZero) => {
    if (value === null || value === undefined || value === "") {
      addFieldError(
        target,
        key,
        t("commons.errors.requiredField", { fieldName: priceLabel })
      );
      return;
    }

    const n = parseFloat(value);
    if (!Number.isFinite(n) || (allowZero ? n < 0 : n <= 0)) {
      addFieldError(
        target,
        key,
        t("commons.errors.invalidNumber", { fieldName: priceLabel })
      );
    }
  };

  const validateStepDraft = () => {
    const errors = { activities: {}, options: {} };
    const invalidActivities = [];
    const invalidOptions = [];

    const titleLabel = t("commons.labels.ranking.title");
    const cityLabel = t("commons.labels.city");
    const countryLabel = t("commons.labels.country");
    const hostLabel = t("commons.labels.host");
    const starsLabel = t("commons.labels.stars");
    const itemTitleLabel = t("commons.labels.title");
    const priceLabel = t("commons.labels.price");

    if (!stepDraft.rankingTitle?.trim()) {
      addFieldError(errors, "rankingTitle", t("commons.errors.requiredField", { fieldName: titleLabel }));
    } else if (stepDraft.rankingTitle.trim().length < 2) {
      addFieldError(errors, "rankingTitle", t("commons.errors.minLength", { fieldName: titleLabel, count: 2 }));
    }

    if (!stepDraft.city?.trim()) {
      addFieldError(errors, "city", t("commons.errors.requiredField", { fieldName: cityLabel }));
    } else if (stepDraft.city.trim().length < 2) {
      addFieldError(errors, "city", t("commons.errors.minLength", { fieldName: cityLabel, count: 2 }));
    }

    if (!stepDraft.country?.trim()) {
      addFieldError(errors, "country", t("commons.errors.requiredField", { fieldName: countryLabel }));
    } else if (stepDraft.country.trim().length < 2) {
      addFieldError(errors, "country", t("commons.errors.minLength", { fieldName: countryLabel, count: 2 }));
    }

    if (stepDraft.stay) {
      if (!stepDraft.hostingName?.trim()) {
        addFieldError(errors, "hostingName", t("commons.errors.requiredField", { fieldName: hostLabel }));
      } else if (stepDraft.hostingName.trim().length < 2) {
        addFieldError(errors, "hostingName", t("commons.errors.minLength", { fieldName: hostLabel, count: 2 }));
      }
      if (!Number(stepDraft.hostingStars)) {
        addFieldError(errors, "hostingStars", t("commons.errors.requiredField", { fieldName: starsLabel }));
      }
    }

    stepDraft.activities.forEach((item, idx) => {
      const itemErrors = {};

      if (!item?.title?.trim()) {
        addFieldError(itemErrors, "title", t("commons.errors.requiredField", { fieldName: itemTitleLabel }));
      } else if (item.title.trim().length < 2) {
        addFieldError(itemErrors, "title", t("commons.errors.minLength", { fieldName: itemTitleLabel, count: 2 }));
      }

      if (!item?.isIncluded) {
        validatePrice(item?.adultPricePerPerson?.value, priceLabel, itemErrors, "adultPricePerPerson", false);
        validatePrice(item?.childPricePerPerson?.value, priceLabel, itemErrors, "childPricePerPerson", true);
        validatePrice(item?.babiesPricePerPerson?.value, priceLabel, itemErrors, "babiesPricePerPerson", true);
      }

      if (Object.keys(itemErrors).length) {
        errors.activities[idx] = itemErrors;
        invalidActivities.push(idx);
      }
    });

    stepDraft.options.forEach((item, idx) => {
      const itemErrors = {};

      if (!item?.title?.trim()) {
        addFieldError(itemErrors, "title", t("commons.errors.requiredField", { fieldName: itemTitleLabel }));
      } else if (item.title.trim().length < 2) {
        addFieldError(itemErrors, "title", t("commons.errors.minLength", { fieldName: itemTitleLabel, count: 2 }));
      }

      validatePrice(item?.adultPricePerPerson?.value, priceLabel, itemErrors, "adultPricePerPerson", false);
      validatePrice(item?.childPricePerPerson?.value, priceLabel, itemErrors, "childPricePerPerson", true);
      validatePrice(item?.babiesPricePerPerson?.value, priceLabel, itemErrors, "babiesPricePerPerson", true);

      if (Object.keys(itemErrors).length) {
        errors.options[idx] = itemErrors;
        invalidOptions.push(idx);
      }
    });

    const hasMainErrors =
      Boolean(errors.rankingTitle?.length) ||
      Boolean(errors.city?.length) ||
      Boolean(errors.country?.length) ||
      Boolean(errors.hostingName?.length) ||
      Boolean(errors.hostingStars?.length);

    const hasSubErrors = invalidActivities.length > 0 || invalidOptions.length > 0;

    return {
      hasErrors: hasMainErrors || hasSubErrors,
      errors,
      invalidActivities,
      invalidOptions,
    };
  };

  const upsertStep = () => {
    const { hasErrors, errors, invalidActivities, invalidOptions } = validateStepDraft();
    setStepErrors(errors);

    if (invalidActivities.length) {
      setExpandedDraftActivities((prev) => ({
        ...prev,
        ...Object.fromEntries(invalidActivities.map((i) => [i, true])),
      }));
    }
    if (invalidOptions.length) {
      setExpandedDraftOptions((prev) => ({
        ...prev,
        ...Object.fromEntries(invalidOptions.map((i) => [i, true])),
      }));
    }

    if (hasErrors) {
      setValidationError(t("tripPublication.addTrip.errors.stepRequiredFields"));
      showToast(t("tripPublication.addTrip.toasts.stepValidationWarning"), "warning");
      return;
    }

    setStepErrors({});
    setValidationError("");

    const existing = editingStepIndex !== null ? tripSteps[editingStepIndex] : null;
    const mapped = {
      ...(existing?.id ? { id: existing.id } : {}),
      ranking: {
        rank: Number(stepDraft.rankingRank) || tripSteps.length + 1,
        title: stepDraft.rankingTitle,
      },
      city: stepDraft.city,
      country: stepDraft.country,
      latitude: nullableNum(stepDraft.latitude),
      longitude: nullableNum(stepDraft.longitude),
      stay: Boolean(stepDraft.stay),
      hosting: {
        name: stepDraft.stay ? stepDraft.hostingName : "",
        stars: stepDraft.stay ? Number(stepDraft.hostingStars) || 0 : 0,
      },
      description: stepDraft.description,
      picture: {
        link: stepDraft.pictureLink,
        isMain: Boolean(stepDraft.pictureLink) && Boolean(stepDraft.isMain),
      },
      activities: stepDraft.activities.map((i) => mapItem(i, true)),
      options: stepDraft.options.map((i) => mapItem(i, false)),
    };

    const targetIndex = editingStepIndex !== null ? editingStepIndex : tripSteps.length;
    const next = editingStepIndex !== null
      ? tripSteps.map((s, i) => (i === targetIndex ? mapped : s))
      : [...tripSteps, mapped];

    const normalized = mapped.picture.isMain
      ? next.map((s, i) => (i === targetIndex ? s : { ...s, picture: { ...s.picture, isMain: false } }))
      : next;

    setTripSteps(normalized.map((s, i) => ({ ...s, ranking: { ...s.ranking, rank: i + 1 } })));
    const idx = editingStepIndex !== null ? editingStepIndex : tripSteps.length;
    setExpandedTripSteps((prev) => ({ ...prev, [idx]: true }));
    setEditingStepIndex(null);
    setStepDraft(emptyDraft());
    setExpandedDraftActivities({});
    setExpandedDraftOptions({});
  };

  const editStep = (idx) => {
    const s = tripSteps[idx];
    if (!s) return;
    setEditingStepIndex(idx);
    setStepDraft({
      rankingTitle: s?.ranking?.title || "",
      rankingRank: s?.ranking?.rank || idx + 1,
      city: s?.city || "",
      country: s?.country || "",
      latitude: nullableNum(s?.latitude),
      longitude: nullableNum(s?.longitude),
      stay: Boolean(s?.stay),
      hostingName: s?.hosting?.name || "",
      hostingStars: s?.hosting?.stars || 0,
      description: s?.description || "",
      pictureLink: s?.picture?.link || "",
      isMain: Boolean(s?.picture?.isMain),
      activities: (s?.activities || []).map((a) => ({ ...emptyItem(true), ...a })),
      options: (s?.options || []).map((o) => ({ ...emptyItem(false), ...o })),
    });
    setStepErrors({});
    setExpandedDraftActivities({});
    setExpandedDraftOptions({});
  };

  const removeStep = async (idx) => {
    const stepToRemove = tripSteps[idx];
    if (stepToRemove?.picture?.link) {
      await removeUploadedByUrl(stepToRemove.picture.link);
    }

    const mediaUrls = [
      ...(stepToRemove?.activities || []).map((item) => item?.picture).filter(Boolean),
      ...(stepToRemove?.options || []).map((item) => item?.picture).filter(Boolean),
    ];
    await Promise.all(mediaUrls.map((url) => removeUploadedByUrl(url)));

    setTripSteps((prev) =>
      prev
        .filter((_, i) => i !== idx)
        .map((s, i) => ({ ...s, ranking: { ...s.ranking, rank: i + 1 } }))
    );
    if (editingStepIndex === idx) {
      setEditingStepIndex(null);
      setStepDraft(emptyDraft());
    }
    setExpandedTripSteps((prev) => {
      const { [idx]: _, ...rest } = prev;
      return rest;
    });
  };

  const toggleTripStepExpand = (idx) => {
    setExpandedTripSteps((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const toggleDraftItemExpand = (group, idx) => {
    if (group === "activities") {
      setExpandedDraftActivities((prev) => ({ ...prev, [idx]: !prev[idx] }));
    } else {
      setExpandedDraftOptions((prev) => ({ ...prev, [idx]: !prev[idx] }));
    }
  };

  const moveStep = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex === null || toIndex === null) return;
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= tripSteps.length || toIndex >= tripSteps.length) return;

    setTripSteps((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next.map((s, i) => ({ ...s, ranking: { ...s.ranking, rank: i + 1 } }));
    });

    if (editingStepIndex !== null) {
      if (editingStepIndex === fromIndex) {
        setEditingStepIndex(toIndex);
      } else if (fromIndex < toIndex && editingStepIndex > fromIndex && editingStepIndex <= toIndex) {
        setEditingStepIndex(editingStepIndex - 1);
      } else if (toIndex < fromIndex && editingStepIndex >= toIndex && editingStepIndex < fromIndex) {
        setEditingStepIndex(editingStepIndex + 1);
      }
    }

    setExpandedTripSteps({});
  };

  const cleanObject = (obj) => {
    if (Array.isArray(obj)) return obj.map(cleanObject);
    if (obj !== null && typeof obj === "object") {
      const cleaned = {};
      Object.keys(obj).forEach((key) => {
        if (key === "__typename" || key === "establishment") return;
        const value = cleanObject(obj[key]);
        if (key !== "trip") cleaned[key] = value;
      });
      return cleaned;
    }
    return obj;
  };

  const mapTripProgramDetailInput = (step) => {
    const { activities, options, ranking, hosting, picture, latitude, longitude, ...rest } = step;
    const sanitizeItem = (item, isActivity) => ({
      ...(item?.id ? { id: item.id } : {}),
      title: item?.title || "",
      description: item?.description || "",
      picture: item?.picture || "",
      ...(isActivity ? { isIncluded: Boolean(item?.isIncluded) } : {}),
      adultPricePerPerson: {
        value: num(item?.adultPricePerPerson?.value),
        currency: item?.adultPricePerPerson?.currency || "MAD",
      },
      childPricePerPerson: {
        value: num(item?.childPricePerPerson?.value),
        currency: item?.childPricePerPerson?.currency || "MAD",
      },
      babiesPricePerPerson: {
        value: num(item?.babiesPricePerPerson?.value),
        currency: item?.babiesPricePerPerson?.currency || "MAD",
      },
    });

    return {
      ...rest,
      latitude: nullableNum(latitude),
      longitude: nullableNum(longitude),
      ranking: {
        rank: Number(ranking?.rank) || 0,
        title: ranking?.title || "",
      },
      hosting: {
        name: hosting?.name || "",
        stars: Number(hosting?.stars) || 0,
      },
      picture: {
        link: picture?.link || "",
        isMain: Boolean(picture?.isMain),
      },
      activities: (activities || []).map((item) => sanitizeItem(item, true)),
      options: (options || []).map((item) => sanitizeItem(item, false)),
    };
  };

  const handleSubmit = async () => {
    if (!establishment?.id) {
      showToast(t("tripPublication.addTrip.errors.selectEstablishment"), "warning");
      return;
    }

    if (!generalInfo.title?.trim()) {
      showToast(t("tripPublication.addTrip.errors.addTitle"), "warning");
      return;
    }

    if (!tripSteps.length) {
      showToast(t("tripPublication.addTrip.errors.addStep"), "warning");
      return;
    }

    const newTrip = {
      establishmentId: establishment.id,
      reference: loadedTrip?.reference || buildReference(generalInfo.title),
      title: generalInfo.title,
      smallDescription: generalInfo.smallDescription,
      detailedDescription: generalInfo.description,
      tripProgramDetails: tripSteps.map(mapTripProgramDetailInput),
    };

    const redirectToPublish = ({ hasStepStructureChanged = false } = {}) => {
      if (publicationId) {
        navigate(`/admin/circuits/${publicationId}/publish`, {
          state: { fromTripEdit: true, hasStepStructureChanged },
        });
        return;
      }
      navigate("/admin/circuits");
    };

    if (trip) {
      const loadedStepIds = new Set(
        (loadedTrip?.tripProgramDetails || []).map((step) => step?.id).filter(Boolean)
      );
      const currentStepIds = new Set((tripSteps || []).map((step) => step?.id).filter(Boolean));
      const hasNewStepAdded = (tripSteps || []).some(
        (step) => !step?.id || !loadedStepIds.has(step.id)
      );
      const hasStepRemoved = Array.from(loadedStepIds).some((id) => !currentStepIds.has(id));
      const hasStepStructureChanged = hasNewStepAdded || hasStepRemoved;

      const oldTrip = {
        id: loadedTrip?.id,
        establishmentId: loadedTrip?.establishmentId,
        reference: loadedTrip?.reference,
        title: loadedTrip?.title,
        smallDescription: loadedTrip?.smallDescription,
        detailedDescription: loadedTrip?.detailedDescription,
        tripProgramDetails: (loadedTrip?.tripProgramDetails || []).map(mapTripProgramDetailInput),
      };
      newTrip.id = loadedTrip?.id;

      if (isEqual(oldTrip, newTrip)) {
        showToast(t("tripPublication.addTrip.toasts.noChanges"), "warning");
        redirectToPublish({ hasStepStructureChanged: false });
        return;
      }
    }

    try {
      if (trip) {
        const loadedStepIds = new Set(
          (loadedTrip?.tripProgramDetails || []).map((step) => step?.id).filter(Boolean)
        );
        const currentStepIds = new Set((tripSteps || []).map((step) => step?.id).filter(Boolean));
        const hasNewStepAdded = (tripSteps || []).some(
          (step) => !step?.id || !loadedStepIds.has(step.id)
        );
        const hasStepRemoved = Array.from(loadedStepIds).some((id) => !currentStepIds.has(id));
        const hasStepStructureChanged = hasNewStepAdded || hasStepRemoved;

        const { error } = await updateTrip(loadedTrip.id, cleanObject(newTrip));
        if (!error) {
          triggerRefresh();
          showToast(t("tripPublication.addTrip.toasts.updated"), "success");
          redirectToPublish({ hasStepStructureChanged });
          return;
        }

        showToast(t("tripPublication.addTrip.toasts.updateError"), "error");
        return;
      }

      const { error } = await addTrip(newTrip);
      if (!error) {
        triggerRefresh();
        navigate("/admin/circuits", {
          state: {
            toast: {
              messageKey: "tripPublication.addTrip.toasts.created",
              type: "success",
            },
          },
        });
        return;
      }

      showToast(t("tripPublication.addTrip.toasts.createError"), "error");
    } catch (error) {
      showToast(
        t(trip ? "tripPublication.addTrip.toasts.updateError" : "tripPublication.addTrip.toasts.createError"),
        "error"
      );
    }
  };

  if (trip && loading && !loadedTrip) {
    return <Preloader />;
  }

  return (
    <div
      className={`${globalStyles.container_fluid} ${styles.pageRoot}`}
    >
      <div className={styles.container}>
        <div className={styles.mainCard}>
          <div className={styles.cardHeader}>
            <div>
              <Text as="h3" variant="heading" size="lg" weight="semibold">
                {t("commons.labels.tripConfigurationTitle")}
              </Text>
              <Text as="p" size="sm" color="muted" className={styles.subtitle}>
                {t("commons.labels.tripConfigurationSubtitle")}
              </Text>
            </div>
          </div>

          <div className={styles.tabsNav}>
            <Button
              className={`${styles.tabBtn} ${activeTab === TABS.GENERAL ? styles.tabActive : ""}`}
              label={t("tripPublication.addTrip.tabs.general")}
              onClick={() => setActiveTab(TABS.GENERAL)}
              iconName="iconInfo"
              iconPosition="left"
              variant="ghost"
              size="medium"
            />
            <Button
              className={`${styles.tabBtn} ${activeTab === TABS.PROGRAM ? styles.tabActive : ""} ${!hasEstablishment ? styles.tabDisabled : ""}`}
              label={t("tripPublication.addTrip.tabs.program")}
              onClick={() => setActiveTab(TABS.PROGRAM)}
              disabled={!hasEstablishment}
              iconName="iconLocation"
              iconPosition="left"
              variant="ghost"
              size="medium"
            />
          </div>

          <div className={styles.cardBody}>
            {activeTab === TABS.GENERAL && (
              <GeneralInfoCard
                establishments={establishments}
                establishment={establishment}
                setEstablishment={setEstablishment}
                generalInfo={generalInfo}
                setGeneralInfo={setGeneralInfo}
                onContinue={() => setActiveTab(TABS.PROGRAM)}
                continueDisabled={!hasEstablishment}
              />
            )}

            {activeTab === TABS.PROGRAM && (
              <div className={styles.programFormCard}>
                <ProgramStepFormCard
                  editingStepIndex={editingStepIndex}
                  upsertStep={upsertStep}
                  validationError={validationError}
                  stepErrors={stepErrors}
                  stepDraft={stepDraft}
                  updateDraft={updateDraft}
                  addSubItem={addSubItem}
                  expandedDraftActivities={expandedDraftActivities}
                  toggleDraftItemExpand={toggleDraftItemExpand}
                  removeSubItem={removeSubItem}
                  updateSubField={updateSubField}
                  toggleActivityIncluded={toggleActivityIncluded}
                  updateSubPrice={updateSubPrice}
                  expandedDraftOptions={expandedDraftOptions}
                />
              </div>
            )}
          </div>
        </div>

        {activeTab === TABS.PROGRAM && tripSteps.length > 0 && (
          <div className={styles.secondaryCard}>
            <ItineraryStepsCard
              tripSteps={tripSteps}
              draggedStepIndex={draggedStepIndex}
              setDraggedStepIndex={setDraggedStepIndex}
              moveStep={moveStep}
              expandedTripSteps={expandedTripSteps}
              toggleTripStepExpand={toggleTripStepExpand}
              editStep={editStep}
              removeStep={removeStep}
              trip={trip}
              handleSubmit={handleSubmit}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AddTrip;
